import React, { useMemo } from 'react';
import { UpgradeLink } from '../UpgradeLink';
import type { TickerStats } from '../../services/optionContractsService';
import type { SymbolHeader } from '../../services/symbolSummaryService';
import type { SymbolData } from '../../types/marketData';

/**
 * The three stat columns above the chart: gamma walls, price context and
 * aggregate options flow.
 *
 * Laid out as columns rather than tiles so each group reads as a unit and the
 * row stays one screen high however many stats land in it.
 */

/* ── shared chrome ──────────────────────────────────────────────────── */

function fmtPrice(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/**
 * A signed percentage — `+37.98%`, `−4.29%`.
 *
 * Uses a true minus sign rather than a hyphen, matching `fmtMoney`, so the two
 * line up when they sit in adjacent tiles.
 */
function fmtSignedPct(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(2)}%`;
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const body =
    abs >= 1e9
      ? `$${(abs / 1e9).toFixed(2)}B`
      : abs >= 1e6
        ? `$${(abs / 1e6).toFixed(1)}M`
        : abs >= 1e3
          ? `$${Math.round(abs / 1e3)}K`
          : `$${Math.round(abs)}`;
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${body}`;
}

function Column({ title, meta, children, listBody = true }: {
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Wrap the body in a `<dl>` of label/value rows — true for every column
   * built from `Stat`.
   *
   * ⚠️ Options Flow sets this false. Its body is tiles, a bar and a grouped
   * block rather than a definition list, and forcing that markup into a `<dl>`
   * would nest non-`dt`/`dd` children in a list that promises otherwise, plus
   * inherit a `space-y` meant for single-line rows.
   */
  listBody?: boolean;
}): React.ReactElement {
  // The background is load-bearing: the grid in Overview.tsx shows a
  // border-coloured background through a 1px gap to draw the dividers, so an
  // opaque cell background is what keeps that gap to a hairline.
  return (
    <section className="p-4 bg-dark-surface">
      {/* Rule under the title separates the heading from the rows. The table
          panels get this for free from their own header row. */}
      <div className="flex items-baseline gap-2 pb-2 mb-3 flex-wrap border-b border-dark-border">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">{title}</h2>
        {meta}
      </div>
      {listBody ? <dl className="space-y-1.5">{children}</dl> : children}
    </section>
  );
}

function Stat({ label, value, tone = 'text-white', note }: {
  label: string;
  value: string;
  tone?: string;
  /** Small marker rendered after the value — used to flag sample data. */
  note?: React.ReactNode;
}): React.ReactElement {
  // gray-200 sits one step below the branded white: enough to let the value
  // lead without the label sliding back into the murk. gray-500, which this
  // started as, was darker than the spec's own Text Muted.
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[11px] uppercase tracking-wider text-gray-200 whitespace-nowrap">
        {label}
      </dt>
      <dd className={`font-numeric text-sm font-medium flex items-baseline gap-1.5 ${tone}`}>
        {value}
        {note}
      </dd>
    </div>
  );
}

/**
 * Groups rows inside a column. Dashed rather than solid so it reads as a
 * lighter break than the rule under the heading, which separates chrome from
 * data — this one only separates data from data.
 */
function DashedDivider(): React.ReactElement {
  return <div className="border-t border-dashed border-dark-border my-2" />;
}

interface PrevCloseRange {
  low: number;
  high: number;
  /** Where the previous close sits across the band, 0–100. */
  pct: number;
  /** Percent below the high. Positive if the close set a new one. */
  fromHighPct: number;
  /** Percent above the low. Negative if the close set a new one. */
  fromLowPct: number;
}

/**
 * The 52-week band and where the PREVIOUS CLOSE sits in it.
 *
 * ⚠️ **Deliberately not `headerRange`, which measures the LIVE price.** This
 * column's readout is labelled Prev Close, so the marker, the percentage and
 * both distances all have to come from that same figure — a marker driven by
 * `last` under a number reading `prevClose` would put the dot somewhere the
 * number does not explain.
 *
 * ⚠️ **The band is refreshed roughly daily and can lag a fresh extreme.** Both
 * edges are widened to admit the close rather than clamping `pct` alone:
 * clamping only the meter would pin the dot at 100% while "From High" showed a
 * positive percentage, which reads as a rendering fault. The two distances are
 * measured against the UNWIDENED edges so a new extreme reads honestly instead
 * of being flattened to zero.
 */
function prevCloseRange(h: SymbolHeader): PrevCloseRange | null {
  const { week52Low: lo, week52High: hi, prevClose: close } = h;
  if (lo == null || hi == null || close == null) return null;

  const low = Math.min(lo, close);
  const high = Math.max(hi, close);
  const span = high - low;

  return {
    low,
    high,
    // A flat year would divide by zero; treat it as sitting at the top.
    pct: span > 0 ? ((close - low) / span) * 100 : 100,
    fromHighPct: hi > 0 ? (close / hi - 1) * 100 : 0,
    fromLowPct: lo > 0 ? (close / lo - 1) * 100 : 0,
  };
}

/**
 * The 52-week band as a track, with the previous close marked on it.
 *
 * The track is the whole span and is always full — red at the low end, blue at
 * the high — so the gradient reads as position within the year rather than as
 * a quantity filled. The dot is the close.
 *
 * ⚠️ The readout is clamped away from the track's ends. Centred on the marker,
 * a close sitting at 2% or 98% would push its own label off the side of the
 * card; pinning it inside keeps it readable while the dot stays exactly where
 * it belongs.
 */
function RangeTrack({ pct, value }: {
  pct: number;
  value: string;
}): React.ReactElement {
  const dotAt = Math.min(100, Math.max(0, pct));
  const labelAt = Math.min(88, Math.max(12, dotAt));

  return (
    <div>
      <div className="relative h-5">
        <div
          className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-center"
          style={{ left: `${labelAt}%` }}
        >
          <span className="text-[9px] uppercase tracking-wider text-gray-400">Prev Close </span>
          <span className="font-numeric text-xs font-semibold text-accent-yellow">{value}</span>
        </div>
      </div>

      <div className="relative h-2 rounded-full bg-gradient-to-r from-bear-red via-gray-600 to-bull-blue">
        {/* Ringed in the card's own surface so the dot reads as sitting on the
            track rather than punched through it. */}
        <span
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-accent-yellow border-2 border-dark-surface"
          style={{ left: `${dotAt}%` }}
          title={`${pct.toFixed(0)}% of the 52-week range`}
        />
      </div>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-gray-300 text-sm">{children}</p>;
}

/* ── gamma levels ───────────────────────────────────────────────────── */

export function GammaLevelsColumn({ symbol, levels, hasAccess }: {
  symbol: string;
  levels: SymbolData | undefined;
  hasAccess: boolean;
}): React.ReactElement {
  const gex = levels?.gex_levels;

  // The feed answers for symbols it has no gamma data on, returning a row
  // whose walls are all null. Four dashes read as a fault; say plainly that
  // there is nothing published instead.
  const hasWalls =
    gex != null &&
    [gex.call_wall_major, gex.call_wall_minor, gex.put_wall_minor, gex.put_wall_major].some(
      (v) => v != null
    );

  return (
    <Column title="Gamma Levels">
      {!hasAccess ? (
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <svg className="w-4 h-4 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="4" y="11" width="16" height="9" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" strokeLinecap="round" />
          </svg>
          <span>Requires a Fuzion subscription.</span>
          <UpgradeLink />
        </div>
      ) : !hasWalls ? (
        <Note>No gamma levels published for {symbol}</Note>
      ) : (
        <>
          {/* Major before minor on BOTH sides, so the two halves read the same
              way down.

              ⚠️ This deliberately replaces a high-to-low "price ladder"
              ordering, which put the minors either side of the divider. That
              only reads as a ladder if a major wall is always further from
              spot than its minor — which the feed does not guarantee: major
              and minor rank by gamma concentration, not by distance. Ranking
              the rows by what the labels actually mean is the honest order.

              Colour is the side, not the rank — the label already says major
              or minor, and dimming the minors made them look like stale or
              partial data. */}
          <Stat label="Call Wall Major" value={fmtPrice(gex?.call_wall_major)} tone="text-bull-blue" />
          <Stat label="Call Wall Minor" value={fmtPrice(gex?.call_wall_minor)} tone="text-bull-blue" />

          {/* Splits the two sides: calls above, puts below. */}
          <DashedDivider />

          <Stat label="Put Wall Major" value={fmtPrice(gex?.put_wall_major)} tone="text-bear-red" />
          <Stat label="Put Wall Minor" value={fmtPrice(gex?.put_wall_minor)} tone="text-bear-red" />
        </>
      )}
    </Column>
  );
}

/* ── price context ──────────────────────────────────────────────────── */

export function PriceStatsColumn({ symbol, header }: {
  symbol: string;
  header: SymbolHeader | null;
}): React.ReactElement {
  const range = useMemo(() => (header ? prevCloseRange(header) : null), [header]);

  return (
    <Column title="Range" listBody={false}>
      {!range ? (
        <Note>No 52-week history for {symbol}</Note>
      ) : (
        <>
          {/* Same tiles as Volume and Options Flow, so the four columns read as
              one system.

              ⚠️ From High is normally negative and From Low positive — price
              sits inside its own year. The tones follow the sign rather than
              being pinned, so the rare new-high or new-low day inverts them
              instead of quietly rendering the usual colour. */}
          <div className="grid grid-cols-3 gap-2">
            <FlowTile
              label="Of Range"
              value={`${range.pct.toFixed(0)}%`}
              tone="text-accent-yellow"
            />
            {/* Low before high, matching the track beneath: the band runs low
                on the left to high on the right, so the two distances read in
                the same direction as the thing they measure. */}
            <FlowTile
              label="From Low"
              value={fmtSignedPct(range.fromLowPct)}
              tone={range.fromLowPct < 0 ? 'text-bear-red' : 'text-bull-blue'}
            />
            <FlowTile
              label="From High"
              value={fmtSignedPct(range.fromHighPct)}
              tone={range.fromHighPct < 0 ? 'text-bear-red' : 'text-bull-blue'}
            />
          </div>

          <div className="mt-4">
            <RangeTrack pct={range.pct} value={fmtPrice(header?.prevClose)} />
          </div>

          {/* The band's two edges, each under its own end of the track and
              coloured to match it. */}
          <div className="flex items-start justify-between gap-3 mt-2">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-bear-red">52W Low</div>
              <div className="font-numeric text-base font-bold text-white leading-tight">
                {fmtPrice(range.low)}
              </div>
            </div>
            <div className="min-w-0 text-right">
              <div className="text-[9px] uppercase tracking-wider text-bull-blue">52W High</div>
              <div className="font-numeric text-base font-bold text-white leading-tight">
                {fmtPrice(range.high)}
              </div>
            </div>
          </div>
        </>
      )}
    </Column>
  );
}

/* ── options flow ───────────────────────────────────────────────────── */

/**
 * Today's activity as a percentage of the running average.
 *
 * 100% is a normal day; 250% is two and a half times normal. Expressed as a
 * percentage rather than a multiple because it sits directly beneath the two
 * raw counts it is derived from, and reads as their relationship.
 */
function pctOfAvg(current: number | null, avg: number | null): number | null {
  if (current == null || avg == null || avg <= 0) return null;
  return (current / avg) * 100;
}

/**
 * How busy a session is against its own average, in three bands.
 *
 * ⚠️ **Gold is the TOP band here, not a warning.** At or above average is the
 * notable case, so 100%+ takes the accent. Blue covers the ordinary half-to-
 * normal range, and red marks a session running at under half its usual size —
 * unusually quiet, which is a signal in its own right rather than an error.
 *
 * ⚠️ Shared by Volume and Options Flow deliberately: "% of avg" means the same
 * thing in both columns, and a figure that read blue in one and white in the
 * other would look like a bug.
 */
function pctOfAvgTone(pct: number | null): string {
  if (pct == null) return 'text-gray-600';
  if (pct >= 100) return 'text-accent-yellow';
  return pct >= 50 ? 'text-bull-blue' : 'text-bear-red';
}

/**
 * A percentage as a ring, with the figure in the middle.
 *
 * ⚠️ **The arc is capped at a full circle; the number is not.** A session at
 * 251% of average draws the same complete ring as one at exactly 100% — past
 * full there is no more circle to give. The ring answers "at or over normal?"
 * at a glance and the number carries the magnitude, which is why the two are
 * never redundant.
 *
 * Colour comes from `currentColor` rather than a `stroke-*` class, so the arc
 * and the figure are tinted by one class and cannot drift apart.
 */
function RingGauge({ pct, label, tone }: {
  pct: number | null;
  label: string;
  tone: string;
}): React.ReactElement {
  // r=42 inside a 100-box leaves room for a 12-wide stroke without clipping.
  const RADIUS = 42;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const filled = pct == null ? 0 : Math.min(100, Math.max(0, pct)) / 100;

  return (
    <div className="relative mx-auto w-[150px] h-[150px]">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="12"
          stroke="currentColor"
          className="text-dark-bg"
        />
        {/* Rotated so the arc starts at twelve o'clock rather than three. */}
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="12"
          stroke="currentColor"
          strokeLinecap="round"
          className={tone}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - filled)}
          transform="rotate(-90 50 50)"
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-numeric text-2xl font-bold leading-none ${tone}`}>
          {pct == null ? '—' : `${pct.toFixed(0)}%`}
        </span>
        <span className="text-[9px] uppercase tracking-wider text-gray-400 mt-1">{label}</span>
      </div>
    </div>
  );
}

/** Compact share/contract counts: 18.4M, 1.24B, 940K. */
function fmtVolume(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
  return Math.round(n).toLocaleString('en-US');
}

/* ── volume ─────────────────────────────────────────────────────────── */

/**
 * The ticker's share volume, against its own 30-day average.
 *
 * ⚠️ **Both figures come from the same row**, and that is the point: a
 * percentage built from two providers' idea of "volume" can disagree with
 * itself and still render. `relative_volume` is the provider's own ratio and
 * reconciles exactly (29,800,251 / 37,699,309 = 0.79047).
 *
 * The put/call ratio sits here rather than with the options column because it
 * is the reader's second question about volume. It is NOT derived from the
 * contract snapshot — that is a volume-ranked top slice, so a ratio built from
 * it would be biased toward whichever side traded hardest today, which is the
 * very thing the ratio is meant to measure.
 */
export function VolumeColumn({ stats }: {
  stats: TickerStats | null;
}): React.ReactElement {
  // The provider's ratio is a fraction; the label is a percentage.
  const sharePct = stats?.relativeVolume == null ? null : stats.relativeVolume * 100;
  const putCall = stats?.putCall ?? null;

  return (
    <Column title="Volume" listBody={false}>
      {/* The raw figures lead, as tiles — same treatment as Options Flow and
          Range, so the four columns read as one system. */}
      <div className="grid grid-cols-3 gap-2">
        <FlowTile label="Today's Volume" value={fmtVolume(stats?.shareVolume ?? undefined)} />
        <FlowTile label="Avg (30d)" value={fmtVolume(stats?.avgShareVolume ?? undefined)} />
        <FlowTile
          label="Put / Call"
          value={putCall == null ? '—' : putCall.toFixed(2)}
          // Above 1 means more put than call volume — a bearish tilt.
          tone={
            putCall == null ? 'text-gray-600' : putCall > 1 ? 'text-bear-red' : 'text-bull-blue'
          }
        />
      </div>

      {/* ⚠️ The window is named in the ring, not just on the tile above: a
          bare "of avg" leaves the reader to guess which average, and the row
          also carries 7- and 3-day figures. */}
      <div className="mt-4">
        <RingGauge pct={sharePct} label="of 30d Avg" tone={pctOfAvgTone(sharePct)} />
      </div>
    </Column>
  );
}

function premTone(n: number | null): string {
  if (n == null) return 'text-gray-600';
  return n > 0 ? 'text-bull-blue' : n < 0 ? 'text-bear-red' : 'text-gray-400';
}

/**
 * One headline figure in a raised box.
 *
 * Used for the three session totals, which are the numbers read first and so
 * are lifted out of the label/value rows below them. `dark-surface-light` is
 * the palette's raised surface — it must stay lighter than the card behind it
 * or the box reads as a hole rather than a tile.
 */
function FlowTile({ label, value, tone = 'text-white' }: {
  label: string;
  value: string;
  tone?: string;
}): React.ReactElement {
  return (
    <div className="min-w-0 rounded-md border border-dark-border bg-dark-surface-light px-2.5 py-2">
      <div className="text-[9px] uppercase tracking-wider text-gray-400 truncate">{label}</div>
      <div className={`font-numeric text-lg font-bold leading-tight mt-0.5 truncate ${tone}`}>
        {value}
      </div>
    </div>
  );
}

/**
 * Call versus put volume as one split bar.
 *
 * ⚠️ **Shares, not two independent bars.** The two segments always fill the
 * track, so the picture is the SPLIT — which side owns the session — rather
 * than the absolute size of either. Absolute size is what the tiles above
 * already say.
 *
 * Renders nothing when neither side has volume: a bar with no data would be an
 * empty track that reads as a real 0/0 split rather than as absent data.
 */
function CallPutVolumeBar({ callVolume, putVolume }: {
  callVolume: number | null;
  putVolume: number | null;
}): React.ReactElement | null {
  const calls = callVolume ?? 0;
  const puts = putVolume ?? 0;
  const total = calls + puts;
  if (!Number.isFinite(total) || total <= 0) return null;

  const callPct = (calls / total) * 100;

  return (
    <div className="mt-3">
      <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-1.5">
        Call vs Put Volume
      </div>

      {/* One track, two segments — no gap between them, so the boundary itself
          is the reading. */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-dark-bg">
        <div className="bg-bull-blue" style={{ width: `${callPct}%` }} />
        <div className="bg-bear-red" style={{ width: `${100 - callPct}%` }} />
      </div>

      {/* Counts in full, not compacted: these are the two numbers the bar is a
          picture of, and rounding them to 5.1M/2.3M would leave the reader
          unable to check the split they are looking at. */}
      <div className="flex items-baseline justify-between gap-3 mt-1.5">
        <span className="font-numeric text-xs text-bull-blue">{fmtInt(callVolume)}</span>
        <span className="font-numeric text-xs text-bear-red">{fmtInt(putVolume)}</span>
      </div>
    </div>
  );
}

/**
 * A premium row: side swatch, label, signed figure.
 *
 * ⚠️ **The swatch carries the SIDE; the number's colour carries the SIGN.**
 * They are two different facts and each gets one channel — so a call row whose
 * net premium is negative shows a blue swatch beside a red figure, which is
 * exactly the case worth seeing.
 *
 * Colouring the figure by side instead would spend the only colour channel
 * restating what the swatch and the label already say, and would print a
 * positive put premium in red — a number that reads as negative at a glance.
 */
function PremiumRow({ label, value, side, tone }: {
  label: string;
  value: string;
  side: 'call' | 'put';
  tone: string;
}): React.ReactElement {
  return (
    /* Same raised surface as the tiles at the top of the column, so the two
       component rows read as inputs and the unfilled Net row below reads as
       the total they add up to. */
    <div className="flex items-baseline justify-between gap-3 px-2.5 py-1.5 bg-dark-surface-light">
      <span className="flex items-baseline gap-2 min-w-0">
        <span
          aria-hidden
          className={`inline-block w-2 h-2 rounded-sm flex-none translate-y-[-1px] ${
            side === 'call' ? 'bg-bull-blue' : 'bg-bear-red'
          }`}
        />
        <span className="text-[11px] uppercase tracking-wider text-gray-200 truncate">
          {label}
        </span>
      </span>
      <span className={`font-numeric text-sm font-medium whitespace-nowrap ${tone}`}>
        {value}
      </span>
    </div>
  );
}

/**
 * The ticker's whole options session.
 *
 * Every figure here is one row from `options-volume` rather than a sum over
 * the contract snapshot — see the note on `VolumeColumn`. The premiums are
 * UW's **net** figures, already signed for buying versus selling, so heavy put
 * selling correctly pushes the net up rather than down.
 */
export function FlowStatsColumn({ symbol, stats }: {
  symbol: string;
  stats: TickerStats | null;
}): React.ReactElement {
  const optionsPct = pctOfAvg(stats?.totalOptionsVolume ?? null, stats?.avgOptionsVolume ?? null);
  const empty = !!stats && stats.totalOptionsVolume == null && stats.netPremium == null;

  return (
    <Column title="Options Flow" listBody={false}>
      {!stats ? (
        <Note>Waiting for options data&hellip;</Note>
      ) : empty ? (
        <Note>No options activity for {symbol}</Note>
      ) : (
        <>
          {/* Session totals lead, as tiles — they are the figures read first.
              ⚠️ The 30-day window is named on the tile because the row also
              carries 7- and 3-day averages, and "% of Avg" is meaningless
              without knowing which one it is measured against. */}
          <div className="grid grid-cols-3 gap-2">
            <FlowTile label="Total Opt Vol" value={fmtVolume(stats.totalOptionsVolume)} />
            <FlowTile label="Avg (30d)" value={fmtVolume(stats.avgOptionsVolume)} />
            <FlowTile
              label="% of Avg"
              value={optionsPct == null ? '—' : `${optionsPct.toFixed(0)}%`}
              tone={pctOfAvgTone(optionsPct)}
            />
          </div>

          {/* The split that makes those totals up. Replaces the old Call Vol
              and Put Vol rows: the same two numbers, plus the proportion
              between them, which is the part that was being left to the
              reader to work out. */}
          <CallPutVolumeBar callVolume={stats.callVolume} putVolume={stats.putVolume} />

          <div className="mt-3 rounded-md border border-dark-border overflow-hidden">
            <PremiumRow
              label="Call Premium"
              value={fmtMoney(stats.netCallPremium)}
              side="call"
              tone={premTone(stats.netCallPremium)}
            />
            <PremiumRow
              label="Put Premium"
              value={fmtMoney(stats.netPutPremium)}
              side="put"
              tone={premTone(stats.netPutPremium)}
            />

            {/* Net sits inside the same box on the SAME raised surface as the
                two rows above, so the block reads as one object. A darker
                inset was tried and read as a hole punched in the card. The
                rule and the larger type are what mark it as the total — the
                surface colour does not need to say it a third time. */}
            <div className="flex items-baseline justify-between gap-3 px-2.5 py-2 bg-dark-surface-light border-t border-dark-border">
              {/* ⚠️ The empty span is a SPACER, not a stray element. The rows
                  above indent their labels by a 2×2 swatch plus a gap-2, so
                  without it "Net Premium" starts 16px to the left of "Call
                  Premium" and the block's left edge zig-zags. Reserving the
                  swatch's box keeps the three labels on one line without
                  giving Net a dot it has no side to justify. */}
              <span className="flex items-baseline gap-2 min-w-0">
                <span aria-hidden className="inline-block w-2 flex-none" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-white truncate">
                  Net Premium
                </span>
              </span>
              <span
                className={`font-numeric text-xl font-bold whitespace-nowrap ${premTone(stats.netPremium)}`}
              >
                {fmtMoney(stats.netPremium)}
              </span>
            </div>
          </div>
        </>
      )}
    </Column>
  );
}
